import React, { useMemo, useState } from "react";

import { CoverCropDecision } from "services";

import { useDecideCoverCropDetections, useGetFieldsByFarmId, useGetPendingCoverCropDetections, useTranslation } from "utils";

import { FieldListField } from "models";

import { Button, Drawer, Flex, Message, Modal, Typography } from "components";

import { CoverCropWizardStep } from "pages/app/all-field-view/components/tasksDrawer/CoverCropWizardStep";
import {
  DraftDecision,
  DraftsByDetectionId,
  buildDecisionsPayload,
  countCompleteDrafts,
  getFieldIds,
  getSeasonYears,
} from "pages/app/all-field-view/components/tasksDrawer/coverCropDetectionUtils";

type CoverCropDetectionDrawerProps = {
  visible: boolean;
  onClose: () => void;
  farmId?: string;
};

export const CoverCropDetectionDrawer = ({ visible, onClose, farmId }: CoverCropDetectionDrawerProps) => {
  const { t } = useTranslation("allFieldView", { useSuspense: false });

  const { data: suggestions = [], isLoading } = useGetPendingCoverCropDetections({ farmId, enabled: visible });
  // useGetFieldsByFarmId, not useGetFields: same data, but its query key matches the one AllFieldView
  // already populated, so opening the drawer is a cache hit rather than a refetch of the whole farm.
  const { data: fields = [] } = useGetFieldsByFarmId({ farmId: farmId ?? "", enabled: visible });

  /**
   * Every answer the farmer has given, keyed by detectionId. This lives here — above the year tabs and
   * above the cards — so that switching tabs is a pure render filter and one Save can submit the lot.
   */
  const [drafts, setDrafts] = useState<DraftsByDetectionId>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showBulkAbsentModal, setShowBulkAbsentModal] = useState(false);

  const fieldIds = useMemo(() => getFieldIds(suggestions), [suggestions]);
  const fieldsById = useMemo(() => new Map<string, FieldListField>(fields.map((field) => [field.id, field])), [fields]);
  const seasonYears = useMemo(() => getSeasonYears(suggestions), [suggestions]);
  const completeCount = useMemo(() => countCompleteDrafts(suggestions, drafts), [suggestions, drafts]);

  const { mutateAsync: decideCoverCrops } = useDecideCoverCropDetections({ farmId, fieldIds });

  const updateDraft = (detectionId: string, changes: Partial<DraftDecision>) =>
    setDrafts((current) => ({ ...current, [detectionId]: { ...current[detectionId], ...changes } }));

  const submit = async (decisions: CoverCropDecision[]) => {
    if (!decisions.length) return;

    setIsSaving(true);
    try {
      // One request for every year and every field — the API answers 200 even when individual items fail,
      // so the outcomes have to be inspected rather than assumed.
      const { results } = await decideCoverCrops({ decisions });
      const failed = results.filter(({ outcome }) => outcome === "error");

      Message(
        failed.length
          ? { content: t("coverCropDetectionDrawer.savePartialError", { count: failed.length }), icon: "error" }
          : { content: t("coverCropDetectionDrawer.saveSuccess", { count: decisions.length }), icon: "tick" }
      );

      setDrafts({});
      onClose();
    } catch {
      Message({ content: t("coverCropDetectionDrawer.saveError", { defaultValue: "Something went wrong. Your cover crops were not saved." }), icon: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const save = () => submit(buildDecisionsPayload(suggestions, drafts));

  const markAllAbsent = () => {
    setShowBulkAbsentModal(false);

    return submit(suggestions.map(({ detectionId }) => ({ detectionId, decision: "confirmed_absent" as const })));
  };

  const footer = (
    <Flex vertical gap="0.75rem">
      <Flex gap="1rem">
        <Button type="secondary" block disabled={isSaving} onClick={onClose} dataTestId="cover-crop-back">
          {t("coverCropDetectionDrawer.back")}
        </Button>
        <Button type="primary" block loading={isSaving} disabled={completeCount === 0} onClick={save} dataTestId="cover-crop-save">
          {t("coverCropDetectionDrawer.save", { count: completeCount })}
        </Button>
      </Flex>
      <Typography
        variant="paragraph-m"
        className="margin-0 pointer cover-crop-bulk-absent-link"
        onClick={() => setShowBulkAbsentModal(true)}
        data-testid="cover-crop-bulk-absent"
      >
        {t("coverCropDetectionDrawer.noCoverCropOnAny")}
      </Typography>
    </Flex>
  );

  return (
    <>
      <Drawer
        title={t("tasksDrawer.tasks.coverCropDetections.drawer.title")}
        visible={visible}
        onClose={onClose}
        loading={isLoading}
        className="cover-crop-detection-drawer"
        footer={footer}
      >
        <CoverCropWizardStep suggestions={suggestions} seasonYears={seasonYears} drafts={drafts} fieldsById={fieldsById} onChange={updateDraft} />
      </Drawer>

      <Modal
        showModal={showBulkAbsentModal}
        title={t("coverCropDetectionDrawer.bulkAbsentModal.title")}
        description={t("coverCropDetectionDrawer.bulkAbsentModal.description", { count: suggestions.length })}
        okText={t("coverCropDetectionDrawer.bulkAbsentModal.confirm")}
        cancelText={t("coverCropDetectionDrawer.bulkAbsentModal.cancel")}
        onOk={markAllAbsent}
        onCancel={() => setShowBulkAbsentModal(false)}
        dataTestId="cover-crop-bulk-absent-modal"
      />
    </>
  );
};
